import {
  Empty,
  Flex,
  Image,
  List,
  message,
  Popconfirm,
  Spin,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Icon from '@ant-design/icons';
import ActionHeader from '../actionHeader';
import FileUpload from '../fileUpload';
import {
  File,
  TemplateResourceImage,
  TemplateResourceImages,
  UploadBusinessType,
} from '../../types';
import { getTemplateImages } from '../../utils/httpRequest';
import { SVGDelete } from '../../images';
import { ActionButtonWrapper } from '../../styles/commonStyles';

const Section = styled(Flex)`
  padding: 24px;
  background: #fff;
`;

const ResourceTabs = styled(Tabs)`
  height: 100%;

  .ant-tabs-nav {
    position: sticky;
    top: 0;
    z-index: 1;
    margin-bottom: 16px;
    background: #fff;
  }

  .ant-tabs-content-holder {
    overflow-y: auto;
  }

  .ant-tabs-content,
  .ant-tabs-tabpane {
    height: 100%;
  }
`;

const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

const getFileExtension = (file: File) => {
  const source = file.filename || file.url || '';
  const nameWithoutQuery = source.split('?')[0];
  const extension = nameWithoutQuery.split('.').pop();

  return extension && extension !== nameWithoutQuery
    ? extension.toLowerCase()
    : 'file';
};

const FileThumbnail = ({ file }: { file: File }) => {
  const extension = getFileExtension(file);
  const isImage = imageExtensions.includes(extension);

  if (isImage) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{ position: 'relative', width: 48, height: 48 }}
      >
        <Image
          src={file.url}
          alt={file.filename}
          width={48}
          height={48}
          fallback="/placeholder.png"
          style={{ borderRadius: 4, objectFit: 'cover' }}
        />
      </Flex>
    );
  }

  return (
    <a
      href={file.url}
      target="_blank"
      rel="noreferrer"
      style={{ display: 'block' }}
    >
      <Flex
        align="center"
        justify="center"
        style={{
          width: 48,
          height: 48,
          borderRadius: 4,
          background: '#f5f5f5',
          border: '1px solid #d9d9d9',
        }}
      >
        <Typography.Text
          strong
          style={{ maxWidth: 40, fontSize: 11, textTransform: 'uppercase' }}
          ellipsis
        >
          {extension}
        </Typography.Text>
      </Flex>
    </a>
  );
};

const ImageGallery = ({
  title,
  images,
}: {
  title: string;
  images?: TemplateResourceImage[];
}) => (
  <Flex vertical gap={8}>
    <Typography.Title level={5} style={{ margin: 0 }}>
      {title}
    </Typography.Title>
    {images?.length ? (
      <Image.PreviewGroup>
        <Flex wrap gap={12}>
          {images.map((image) => (
            <Flex
              key={image.id}
              vertical
              gap={6}
              style={{ width: 140, minWidth: 140 }}
            >
              <Image
                src={image.url}
                alt={image.filename}
                width={140}
                height={140}
                fallback="/placeholder.png"
                style={{ objectFit: 'cover' }}
              />
              <Tooltip title={image.name || image.filename}>
                <Typography.Text ellipsis>
                  {image.name || image.filename}
                </Typography.Text>
              </Tooltip>
            </Flex>
          ))}
        </Flex>
      </Image.PreviewGroup>
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No images" />
    )}
  </Flex>
);

interface ResourceLibraryProps {
  documents?: File[];
  templateID?: string;
  uploadBusinessType: UploadBusinessType;
  onUpdateDocuments: (documents: File[]) => Promise<void>;
}

const ResourceLibrary = ({
  documents = [],
  templateID,
  uploadBusinessType,
  onUpdateDocuments,
}: ResourceLibraryProps) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('files');
  const [resourceImages, setResourceImages] =
    useState<TemplateResourceImages>();

  useEffect(() => {
    const fetchTemplateImages = async () => {
      if (!templateID) {
        setResourceImages(undefined);
        return;
      }

      try {
        setImagesLoading(true);
        const res = await getTemplateImages(templateID);
        setResourceImages(res.data);
      } catch (err: any) {
        messageApi.error(err || 'Load Images Failed!');
      } finally {
        setImagesLoading(false);
      }
    };

    fetchTemplateImages();
  }, [templateID, messageApi]);

  const handleUploadError = (error: any) => {
    setLoading(false);
    messageApi.error(error || 'Upload Failed!');
  };

  const handleUpdateDocuments = async (
    nextDocuments: File[],
    isDelete?: boolean
  ) => {
    try {
      setLoading(true);
      await onUpdateDocuments(nextDocuments);
      messageApi.success(
        `File ${isDelete ? 'Delete' : 'Upload'} Successfully!`
      );
    } catch (err: any) {
      messageApi.error(err || `${isDelete ? 'Delete' : 'Upload'} Failed!`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = (file: File) => {
    const files = documents.filter((i) => i.id !== file.id);
    handleUpdateDocuments(files, true);
  };

  const handleUploadedFile = async (response: any) => {
    const files = response?.data || [];
    handleUpdateDocuments([...files, ...documents]);
  };

  return (
    <Flex vertical flex={1} gap={0} style={{ height: '100%' }}>
      {contextHolder}
      <ActionHeader
        title="Resource Library"
        isSecondaryTitle
        showBorder={false}
        endSlot={
          activeTab === 'files' ? (
            <FileUpload
              businessType={uploadBusinessType}
              onUploaded={handleUploadedFile}
              onUploading={() => setLoading(true)}
              onError={handleUploadError}
            />
          ) : null
        }
      />
      <Section flex={1} vertical style={{ overflow: 'hidden', paddingTop: 0 }}>
        <ResourceTabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'files',
              label: 'Files',
              children: (
                <Flex vertical gap={16}>
                  <List
                    dataSource={documents}
                    renderItem={(item) => (
                      <List.Item
                        key={item.id}
                        style={{ alignItems: 'center' }}
                        actions={[
                          <Popconfirm
                            key="delete"
                            placement="bottom"
                            title={`Are you sure you want to delete ${item.filename}`}
                            okText="Yes"
                            cancelText="No"
                            onConfirm={() => handleDeleteFile(item)}
                          >
                            <ActionButtonWrapper>
                              <Icon component={SVGDelete} />
                            </ActionButtonWrapper>
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<FileThumbnail file={item} />}
                          style={{ alignItems: 'center' }}
                          title={
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={item.url}
                              style={{ cursor: 'pointer' }}
                            >
                              {item.filename}
                              {'   '}
                              <Typography.Text
                                copyable={{
                                  text: item.url,
                                  tooltips: ['Copy Link'],
                                }}
                              />
                            </a>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </Flex>
              ),
            },
            {
              key: 'images',
              label: 'Image Gallery',
              children: (
                <Spin spinning={imagesLoading}>
                  <Flex vertical gap={24}>
                    <ImageGallery
                      title="Machine Images"
                      images={resourceImages?.machineImages}
                    />
                    <ImageGallery
                      title="Issue Images"
                      images={resourceImages?.issueImages}
                    />
                    <ImageGallery
                      title="Cause Images"
                      images={resourceImages?.causeImages}
                    />
                  </Flex>
                </Spin>
              ),
            },
          ]}
        />
      </Section>
      <Spin fullscreen spinning={loading} />
    </Flex>
  );
};

export default ResourceLibrary;
